import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Container,
} from "@mhome/ui";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import InfoIcon from "@mui/icons-material/Info";
import { useHistory } from "react-router-dom";
import { useToast } from "../common/toast";
import {
  useIsSmallScreen,
  useComponentBackgroundColor,
} from "../common/adaptive-theme-provider";
import { useAppInterface } from "../app/app-interface-provider";

const DesktopSoftwareUpdate = () => {
  const { successToast, warningToast, errorToast } = useToast();
  const appInterface = useAppInterface();
  const [updateStatus, setUpdateStatus] = useState({
    available: false,
    ready: false,
    checking: false,
    version: null,
    info: null,
    releaseNotes: null,
  });
  const [error, setError] = useState(null);
  const [updateTimeout, setUpdateTimeout] = useState(false);
  const timeoutRef = useRef(null);
  const [currentVersion, setCurrentVersion] = useState("Unknown");
  const history = useHistory();
  const isSmallScreen = useIsSmallScreen();
  const [isDownloading, setIsDownloading] = useState(false);
  const [showReadyDialog, setShowReadyDialog] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const hasNewVersion = updateStatus.info && updateStatus.info.version;

  // Get the background color function with current global state
  const getComponentBgColor = useComponentBackgroundColor();

  const saveUpdateInfo = useCallback((info, type) => {
    if (!info || !info.version) return {};
    const updateInfo = {
      version: info.version,
      timestamp: Date.now(),
      type: type,
      ...info,
    };
    localStorage.setItem("mandatoryUpdateInfo", JSON.stringify(updateInfo));
    return updateInfo;
  }, []);

  useEffect(() => {
    const checkStoredUpdateInfo = () => {
      const mandatoryUpdateInfo = localStorage.getItem("mandatoryUpdateInfo");
      if (mandatoryUpdateInfo) {
        try {
          const parsedInfo = JSON.parse(mandatoryUpdateInfo);
          setUpdateStatus({
            available:
              parsedInfo.type === "available" ||
              parsedInfo.type === "downloading",
            ready: parsedInfo.type === "ready",
            version: parsedInfo.version,
            info: parsedInfo,
            checking: false,
            releaseNotes: parsedInfo.releaseNotes,
          });
          setIsDownloading(parsedInfo.type === "downloading");
        } catch (error) {
          console.error("Parse update info error:", error);
          setTimeout(() => errorToast("Failed to parse update information"), 0);
        }
      }
    };

    if (!appInterface.isAvailable()) {
      console.warn(
        "Desktop interface is not available. Update functionality will be limited."
      );
      setTimeout(() => errorToast("Update functionality is disabled"), 0);
      return;
    }

    const handleUpdateAvailable = (info) => {
      if (!info || !info.version) {
        setUpdateStatus((prev) => ({
          ...prev,
          available: false,
          checking: false,
        }));
        warningToast("Update info is undefined or missing version");
        return;
      }
      const savedInfo = saveUpdateInfo(info, "available");
      setUpdateStatus((prev) => ({
        ...prev,
        available: true,
        version: info.version,
        info: savedInfo,
        checking: false,
      }));
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setTimeout(
        () => warningToast(`New version ${info.version} is available`),
        0
      );
    };

    const handleUpdateReady = (info) => {
      if (!info || !info.version) {
        setUpdateStatus((prev) => ({
          ...prev,
          ready: false,
          checking: false,
        }));
        warningToast("Update info is undefined or missing version");
        return;
      }
      const savedInfo = saveUpdateInfo(info, "ready");
      setUpdateStatus((prev) => ({
        ...prev,
        available: true,
        ready: true,
        version: info.version,
        info: savedInfo,
        checking: false,
      }));
      setIsDownloading(false);
      setShowReadyDialog(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setTimeout(
        () => successToast(`Version ${info.version} is ready to install`),
        0
      );
    };

    const handleUpdateNotAvailable = () => {
      setUpdateStatus((prev) => ({
        ...prev,
        checking: false,
      }));
      localStorage.removeItem("mandatoryUpdateInfo");
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setTimeout(() => successToast("Already up to date"), 0);
    };

    const handleUpdateError = (error) => {
      setUpdateStatus((prev) => ({
        ...prev,
        checking: false,
      }));
      setError(error);
      setIsInstalling(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setTimeout(() => errorToast(`Update error: ${error}`), 0);
    };

    if (window.__TAURI_INTERNALS__) {
      window.__TAURI_INTERNALS__
        .invoke("get_current_version")
        .then((version) => {
          setCurrentVersion(version || "Unknown");
        })
        .catch((err) => {
          setTimeout(() => errorToast("Failed to retrieve current version"), 0);
        });
    }

    appInterface.addEventListener("update:available", handleUpdateAvailable);
    appInterface.addEventListener("update:ready", handleUpdateReady);
    appInterface.addEventListener(
      "update:not-available",
      handleUpdateNotAvailable
    );
    appInterface.addEventListener("update:error", handleUpdateError);
    checkStoredUpdateInfo();

    return () => {
      appInterface.removeEventListener(
        "update:available",
        handleUpdateAvailable
      );
      appInterface.removeEventListener("update:ready", handleUpdateReady);
      appInterface.removeEventListener(
        "update:not-available",
        handleUpdateNotAvailable
      );
      appInterface.removeEventListener("update:error", handleUpdateError);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [saveUpdateInfo, errorToast, warningToast, successToast]);

  const checkForUpdates = () => {
    if (!appInterface.isAvailable()) {
      setTimeout(() => errorToast("Update functionality is not available"), 0);
      return;
    }

    try {
      setUpdateStatus((prev) => ({ ...prev, checking: true }));
      setError(null);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      appInterface.checkForUpdates();
      timeoutRef.current = setTimeout(() => {
        setUpdateStatus((prev) => ({ ...prev, checking: false }));
        setUpdateTimeout(true);
        timeoutRef.current = null;
      }, 15000);
    } catch (error) {
      setTimeout(() => errorToast("Failed to check for updates"), 0);
      setUpdateStatus((prev) => ({ ...prev, checking: false }));
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  };

  const handleInstallClick = () => {
    if (updateStatus.info.type === "ready") {
      setShowReadyDialog(true);
    } else {
      setIsDownloading(true);
      const downloadingInfo = { ...updateStatus.info, type: "downloading" };
      localStorage.setItem(
        "mandatoryUpdateInfo",
        JSON.stringify(downloadingInfo)
      );
      setUpdateStatus((prev) => ({
        ...prev,
        info: downloadingInfo,
      }));
      appInterface.downloadUpdate();
    }
  };

  const handleConfirmInstall = () => {
    if (isInstalling) return;
    setError(null);
    setIsInstalling(true);
    installAndRestart();
  };

  const installAndRestart = () => {
    if (appInterface.isAvailable()) {
      localStorage.removeItem("mandatoryUpdateInfo");
      appInterface.installUpdate();
    }
  };

  const handleBackClick = () => {
    history.replace("/setting");
  };

  useEffect(() => {
    if (updateTimeout) {
      errorToast("Update check timed out, please try again later");
      setUpdateTimeout(false);
    }
  }, [updateTimeout]);

  return (
    <div className="flex justify-start items-start flex-col h-full p-4 w-full">
      <div className="flex items-center w-full justify-between flex-row">
        <div className="flex items-center justify-start mb-4">
          {isSmallScreen && (
            <IconButton
              color="inherit"
              edge="start"
              className="mr-2"
              onClick={handleBackClick}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h6" style={{ textAlign: "center" }}>
            Software Update
          </Typography>
        </div>
      </div>
      <Container
        maxWidth="lg"
        style={{
          paddingTop: isSmallScreen ? "8px" : "24px",
          paddingBottom: isSmallScreen ? "8px" : "24px",
        }}
      >
        <div className="flex flex-col gap-8 items-center">
          <Card
            className="w-full mx-auto"
            style={{
              boxShadow: "none",
              backgroundColor: getComponentBgColor("hsl(var(--card))"),
            }}
          >
            <CardContent style={{ padding: "16px" }}>
              <div className="flex flex-col gap-1">
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <InfoIcon
                      style={{ marginRight: "8px", fontSize: "1.2rem" }}
                      color="primary"
                    />
                    <Typography style={{ fontSize: "0.95rem" }}>
                      Current Version:
                    </Typography>
                  </div>
                  <div className="flex items-center">
                    <Typography
                      variant="h6"
                      style={{ fontSize: "1rem", marginRight: "8px" }}
                    >
                      {currentVersion}
                    </Typography>
                  </div>
                </div>
                <div>
                  <Divider style={{ marginTop: "8px", marginBottom: "8px" }} />
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <AutorenewIcon
                        style={{ marginRight: "8px", fontSize: "1.2rem" }}
                        color="primary"
                      />
                      <Typography style={{ fontSize: "0.95rem" }}>
                        Version Update:
                      </Typography>
                    </div>
                    <div className="flex items-center">
                      <Typography
                        variant="h6"
                        style={{
                          fontSize: "1rem",
                          color: "hsl(var(--primary))",
                          marginRight: "8px",
                        }}
                      >
                        {!hasNewVersion
                          ? "Currently up to date"
                          : `New version available${
                              updateStatus.version
                                ? " " + updateStatus.version
                                : ""
                            }`}
                      </Typography>
                      {updateStatus.available && (
                        <div
                          style={{
                            backgroundColor: "hsl(var(--destructive))",
                            color: "white",
                            paddingLeft: "8px",
                            paddingRight: "8px",
                            paddingTop: "4px",
                            paddingBottom: "4px",
                            fontSize: "0.75rem",
                            borderRadius: "4px",
                          }}
                        >
                          New
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div
                  className="flex justify-center gap-2"
                  style={{ marginTop: "24px" }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={checkForUpdates}
                    disabled={updateStatus.checking}
                    style={{
                      paddingLeft: isSmallScreen ? "16px" : "24px",
                      paddingRight: isSmallScreen ? "16px" : "24px",
                      fontSize: isSmallScreen ? "0.875rem" : "1rem",
                      fontWeight: 500,
                    }}
                  >
                    {updateStatus.checking ? (
                      <CircularProgress size={24} />
                    ) : (
                      "Check for Updates"
                    )}
                  </Button>
                  {hasNewVersion && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<DownloadIcon />}
                      onClick={handleInstallClick}
                      disabled={isDownloading}
                      style={{
                        paddingLeft: isSmallScreen ? "16px" : "24px",
                        paddingRight: isSmallScreen ? "16px" : "24px",
                        fontSize: isSmallScreen ? "0.875rem" : "1rem",
                        fontWeight: 500,
                      }}
                    >
                      {isDownloading ? (
                        <span className="flex items-center">
                          <CircularProgress
                            size={20}
                            style={{ marginRight: "8px" }}
                          />
                          Downloading...
                        </span>
                      ) : (
                        "Install & Restart"
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </Container>

      <Dialog
        open={showReadyDialog}
        variant="pops"
        onClose={() => {
          if (!isInstalling) setShowReadyDialog(false);
        }}
      >
        <DialogTitle>Install New Version</DialogTitle>
        <DialogContent>
          The application will restart to complete the installation. Do you want
          to proceed?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowReadyDialog(false)}
            disabled={isInstalling}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmInstall}
            variant="contained"
            disabled={isInstalling}
            startIcon={
              isInstalling ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {isInstalling ? "Installing..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DesktopSoftwareUpdate;
